import {
  LayoutListIcon,
  ActivityIcon,
  ScanSearchIcon,
  GaugeIcon,
  SirenIcon,
  BrainIcon,
  type LucideIcon,
} from "lucide-react";

export type Volume = {
  numeral: string;
  icon: LucideIcon;
  title: string;
  tagline: string;
  intro: string;
  topics: string[];
  objective: string;
  note?: string;
};

export const volumes: Volume[] = [
  {
    numeral: "I",
    icon: LayoutListIcon,
    title: "The Diabetic Foot Assessment Framework",
    tagline: "Organize the key components of an assessment into a logical sequence.",
    intro:
      "I designed this volume to introduce the main elements involved in diabetic foot assessment and help you understand how they fit together. Rather than approaching each finding in isolation, you'll review the information that contributes to a more comprehensive assessment.",
    topics: [
      "Relevant patient history and diabetes-related considerations.",
      "Previous ulceration and amputation history.",
      "General foot inspection and comparison of both feet.",
      "Footwear, mobility, and foot-care considerations.",
      "Skin integrity and visible abnormalities.",
      "Factors that may influence assessment priorities.",
      "Organizing findings into a structured workflow.",
      "Documenting relevant observations clearly.",
      "Recognizing missing information and assessment limitations.",
    ],
    objective:
      "To help you build a more organized foundation for reviewing the different elements of diabetic foot assessment.",
  },
  {
    numeral: "II",
    icon: ActivityIcon,
    title: "Sensory and Circulatory Assessment Reference",
    tagline: "Understand the key concepts behind sensory and vascular assessment.",
    intro:
      "Sensory and circulatory findings are important parts of diabetic foot evaluation. In this volume, I bring together educational references to help you review the main assessment considerations and understand their limitations.",
    topics: [
      "Diabetic peripheral neuropathy concepts.",
      "Symptoms and findings relevant to sensory assessment.",
      "Protective sensation and its clinical significance.",
      "An educational overview of 10 g monofilament testing.",
      "Other sensory examination considerations.",
      "Peripheral pulse assessment concepts.",
      "Skin color, temperature, and other circulatory observations.",
      "An introduction to ankle-brachial index concepts.",
      "Findings that may warrant further vascular evaluation.",
      "Recording sensory and circulatory findings.",
      "Why individual findings should be interpreted in context.",
    ],
    objective:
      "To give you an organized educational reference for reviewing sensory and circulatory assessment concepts.",
  },
  {
    numeral: "III",
    icon: ScanSearchIcon,
    title: "Skin, Nail, and Wound Assessment Atlas",
    tagline: "Organize your observations of skin changes, nail abnormalities, and wounds.",
    intro:
      "Foot findings can vary considerably, and describing what you observe accurately is an important part of clinical documentation. This volume focuses on the characteristics you may need to review and document, while emphasizing that appearance alone does not establish a diagnosis.",
    topics: [
      "General skin inspection considerations.",
      "Calluses, corns, fissures, and pressure-related changes.",
      "Skin dryness and loss of integrity.",
      "Common nail abnormalities.",
      "Blisters and friction-related lesions.",
      "Wound location and descriptive terminology.",
      "Wound dimensions and documentation considerations.",
      "Wound-bed and surrounding-skin observations.",
      "Drainage and other findings that may warrant evaluation.",
      "Possible signs of infection.",
      "Recording changes during follow-up.",
      "Distinguishing observed findings from diagnostic conclusions.",
    ],
    objective:
      "To help you organize your descriptions of relevant foot findings and improve the consistency of your documentation.",
  },
  {
    numeral: "IV",
    icon: GaugeIcon,
    title: "Diabetic Foot Risk Factors and Classification Guide",
    tagline: "Review the factors that contribute to diabetic foot risk assessment.",
    intro:
      "A structured risk assessment considers multiple factors rather than relying on a single symptom or observation. In this volume, I explain the main risk factors and introduce established approaches to risk classification so you can better understand how relevant findings fit into the assessment process.",
    topics: [
      "Previous ulceration and amputation.",
      "Peripheral neuropathy and loss of protective sensation.",
      "Peripheral arterial disease considerations.",
      "Foot deformities and abnormal pressure distribution.",
      "Calluses and recurrent pressure-related lesions.",
      "Footwear and mechanical stress considerations.",
      "Skin integrity and previous wound history.",
      "Introduction to established risk classification frameworks.",
      "Organizing relevant findings before classification.",
      "Documenting the factors that influence risk.",
      "Follow-up and referral considerations.",
      "The importance of applying current guidelines.",
    ],
    objective:
      "To help you organize your understanding of diabetic foot risk factors and classification principles.",
    note: "Risk classification should follow applicable clinical guidelines, professional scope, and local protocols.",
  },
  {
    numeral: "V",
    icon: SirenIcon,
    title: "Clinical Warning Signs and Referral Considerations",
    tagline: "Review concerning findings and understand when further assessment may be necessary.",
    intro:
      "Some diabetic foot presentations require prompt attention. Recognizing potentially concerning findings and understanding when to escalate care are important parts of safe clinical practice. I organized this volume around warning signs, documentation, and general referral considerations.",
    topics: [
      "Concerning changes in foot appearance.",
      "Possible signs of infection.",
      "Increasing redness, warmth, or swelling.",
      "Wounds that deteriorate or fail to heal as expected.",
      "Findings that may suggest impaired circulation.",
      "Tissue discoloration and other concerning observations.",
      "An acutely warm, swollen foot and possible Charcot neuro-osteoarthropathy.",
      "Findings that may require urgent clinical assessment.",
      "General considerations for referral.",
      "Documenting the reason for escalation.",
      "Communicating relevant observations to another clinician.",
      "Understanding why the absence of pain does not necessarily indicate the absence of serious pathology.",
    ],
    objective:
      "To provide an educational reference for reviewing warning signs and the importance of timely clinical assessment.",
    note: "Suspected acute Charcot foot, severe infection, critical ischemia, or another potentially limb-threatening condition requires appropriate urgent clinical evaluation.",
  },
  {
    numeral: "VI",
    icon: BrainIcon,
    title: "The Diabetic Foot Clinical Reasoning Casebook",
    tagline: "Practice organizing assessment information through educational scenarios.",
    intro:
      "I believe that reviewing concepts and practicing how to organize clinical information serve different purposes. That's why this volume brings together educational cases designed to help you apply the assessment principles covered in the other volumes.",
    topics: [
      "Patient history review scenarios.",
      "Cases involving sensory findings.",
      "Circulatory assessment scenarios.",
      "Skin and nail observation exercises.",
      "Wound documentation practice.",
      "Cases involving multiple risk factors.",
      "Guided questions for reviewing clinical findings.",
      "Exercises for identifying missing information.",
      "Examples of structured clinical documentation.",
      "Reflection prompts for organizing your reasoning.",
      "Questions about when further assessment may be appropriate.",
      "Scenarios that reinforce the importance of referral considerations.",
    ],
    objective:
      "To give you opportunities to practice organizing information and reviewing clinical considerations in an educational setting.",
    note: "The scenarios are educational exercises, not patient-specific diagnostic or treatment instructions.",
  },
];
